import React, { useState } from 'react';
import { X, Trash2, Loader2 } from 'lucide-react';

interface ConfirmCancelInviteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
    user: {
        firstName: string;
        lastName: string;
        email: string;
    } | null;
}

const ConfirmCancelInviteModal: React.FC<ConfirmCancelInviteModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    user
}) => {
    const [isCancelling, setIsCancelling] = useState(false);

    const handleConfirm = async () => {
        setIsCancelling(true);
        try {
            await onConfirm();
        } finally {
            setIsCancelling(false);
        }
    };

    if (!isOpen || !user) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-red-50">
                            <Trash2 className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Cancel Invitation</h3>
                            <p className="text-sm text-gray-500">This action cannot be undone</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isCancelling}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-sm text-gray-700 mb-4">
                        Are you sure you want to cancel the invitation for <strong>{user.firstName} {user.lastName}</strong>?
                    </p>

                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
                            <li>The invitation email to <span className="font-mono">{user.email}</span> will be invalidated</li>
                            <li>The pending user account will be permanently deleted</li>
                            <li>You will need to send a new invitation if access is required later</li>
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3 px-6 pb-6">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isCancelling}
                        className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        Go Back
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isCancelling}
                        className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center"
                    >
                        {isCancelling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {isCancelling ? 'Cancelling...' : 'Cancel Invitation'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmCancelInviteModal;
